/**
 * Координаты из ответа API.
 *
 * В базе широта и долгота — `decimal`, без каста в модели, поэтому Laravel
 * отдаёт их строкой: `"51.1694000"`. Проверка `typeof === 'number'` на такой
 * строке молча говорит «координат нет» — у уже заведённого партнёра гаснет
 * кнопка «Адрес по точке», а подпись с точкой не показывается.
 *
 * Приводим один раз на входе в форму. Пустое, `null` и нечисловое — это
 * отсутствие координат, а не ноль: точка (0, 0) лежит в Гвинейском заливе.
 */
export function parseCoordinate(value: unknown): number | undefined {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value !== 'string' || value.trim() === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/**
 * Точка есть, только когда есть обе координаты и это не 0°/0°.
 *
 * Ноль на ноль — след пустых координат, а не заведение в океане; бэк в
 * чек-листе запуска считает так же (`Partner::hasMapPoint`, ADR-014).
 */
export function hasCoordinates(latitude: unknown, longitude: unknown): boolean {
  const lat = parseCoordinate(latitude);
  const lon = parseCoordinate(longitude);
  if (lat === undefined || lon === undefined) {
    return false;
  }
  return lat !== 0 || lon !== 0;
}

/**
 * Долгота в пределах −180…180.
 *
 * Leaflet рисует мир повторами, и клик по соседней копии даёт долготу вроде
 * 431,43: сервер такую отклоняет, а точка залипала бы в черновике с ошибкой
 * при каждой отправке.
 */
export function wrapLongitude(longitude: number): number {
  if (longitude >= -180 && longitude <= 180) {
    return longitude;
  }
  return ((((longitude + 180) % 360) + 360) % 360) - 180;
}
